import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";

/**
 * Who is signed in.
 *
 * Two things here exist because of iOS. Safari suspends a backgrounded page
 * and stops its timers, so the refresh that keeps a session alive does not
 * happen while the phone is locked or the app is behind another one. Coming
 * back, the token may already have expired.
 *
 * So: the session is refreshed whenever the page becomes visible again, and
 * the session is only cleared on an explicit sign-out. Before, any event that
 * arrived with a null session — including transient ones during a refresh —
 * would drop the user to the login screen mid-task.
 */
export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!live) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (!live) return;
      if (event === "SIGNED_OUT") { setSession(null); return; }
      // TOKEN_REFRESHED, SIGNED_IN, USER_UPDATED, INITIAL_SESSION. A null
      // session on any of these is a hiccup, not a sign-out, and throwing the
      // person back to the login screen mid-deal is the worse mistake.
      if (s) setSession(s);
    });

    /* Coming back to the tab: ask for the session again, which refreshes it
       if the access token expired while the page was suspended. */
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      supabase.auth.getSession().then(({ data }) => {
        if (live && data.session) setSession(data.session);
      });
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("pageshow", onVisible);

    return () => {
      live = false;
      sub.subscription.unsubscribe();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, []);

  return { session, loading, signOut: () => supabase.auth.signOut() };
}
